import type { ComponentProps, FC, PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import { ZoneId } from '@js-joda/core';
import { AddContextualData } from '@opendoor/observability';
import { useParams } from '@remix-run/react';

import { OdProtosSellReceptionData_SellerInput_Channel as SellerInputChannel } from '__generated__/globalTypes';

import { DevError } from 'platform/Error';

import type { PartnershipReferralResponse, ReferralStep } from 'growth/usePartnershipReferrals';
import { usePartnershipReferrals } from 'growth/usePartnershipReferrals';

import type { ISellerLeadContext } from 'sellDirect/graphql/SellerLeadContext';
import { useSellerLead } from 'sellDirect/graphql/SellerLeadContext';
import { useGetSellerProductsExperiences } from 'sellDirect/graphql/useGetSellerProductsExperiences';
import type { SellerProducts } from 'sellDirect/graphql/useGetSellerProductsExperiences';
import type { UseParticipantsResult } from 'sellDirect/graphql/useParticipants';
import { useParticipants } from 'sellDirect/graphql/useParticipants';
import { getLeadInfo } from 'sellDirect/helpers/getLeadInfo';

import { useBrowserCompatibilityData } from './globals';

// TODO: This is a temporary fix, we should bring consistency to referral data in partnership service
//  such that all agent affiliated leads have a referral.
const AGENT_CHANNELS = [
  SellerInputChannel.AGENT_REQUEST,
  SellerInputChannel.AGENT_REQUEST_LISTED,
  SellerInputChannel.SELLER_REQUEST_LISTED,
  SellerInputChannel.OUTBOUND_OFFERS_LISTED,
];

export type IQualificationContext = Pick<
  ISellerLeadContext,
  'lead' | 'customerLeads' | 'sellerInputUUID' | 'isBuyOnlyCustomer' | 'isOpendoorOfferQualified'
> & {
  /** ZoneId of the lead's address. */
  zoneId: ZoneId;
  loadingQualification: boolean;
  participantsHelpers: UseParticipantsResult | undefined;
  addressUUID: string | undefined;
  shouldSeeAgentExperience: boolean;
  /** Product qualification and data for the seller */
  sellerProducts: SellerProducts;
  /** Refetch seller products (e.g. to pick up assignment changes) */
  refetchSellerProducts?: ReturnType<typeof useGetSellerProductsExperiences>['refetch'];
  // Partnership Data
  partnershipReferralData?: PartnershipReferralResponse;
  blockBuyerContent: boolean;
  /**
   * When set, represents that the Referral Step that must be completed by the customer.
   * This will be migrated as a step in `sellerJourney`.
   * We skip this step in the Opendoor app Evo web view to keep existing behavior.
   */
  referralStep: ReferralStep | undefined;
};

/** Non-null lead from qualification context. Use this type when a consumer requires lead to be present. */
export type SellerLead = NonNullable<IQualificationContext['lead']>;

/** @internal */
export const defaultLeadContext: IQualificationContext = {
  // Seller Lead
  lead: null,
  customerLeads: [],
  sellerInputUUID: '',
  isBuyOnlyCustomer: false,
  isOpendoorOfferQualified: false,
  // Other
  zoneId: ZoneId.systemDefault(),
  loadingQualification: false,
  blockBuyerContent: false,
  participantsHelpers: undefined,
  addressUUID: undefined,
  shouldSeeAgentExperience: false,
  referralStep: undefined,
  sellerProducts: {
    availableProducts: [],
    selectedProduct: undefined,
    listingProduct: undefined,
    cashOfferProduct: undefined,
    cashPlusProduct: undefined,
    experiences: [],
    previouslyEligibleProducts: [],
  },
};

/** @internal */
export const QualificationContext = createContext<IQualificationContext>(defaultLeadContext);

/**
 * @throws error if qualification context is missing
 */
export const useQualificationContext = () => {
  const value = useContext(QualificationContext);
  if (!value) {
    throw new DevError('QualificationContext missing');
  }
  return value;
};

/**
 * QualificationProvider is a wrapper component that provides the qualification context to its children.
 *
 * @throws error if qualification context is missing
 */
export const QualificationProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    sellerInputUUID,
    lead,
    customerLeads,
    isBuyOnlyCustomer,
    isOpendoorOfferQualified,
    loading: sellerLeadLoading,
  } = useSellerLead();
  const { addressUUID } = useParams();

  const zoneId = lead?.address?.timeZone
    ? ZoneId.of(lead.address.timeZone)
    : ZoneId.systemDefault();

  // Get participant data + methods
  const participantsHelpers = useParticipants(sellerInputUUID);

  const {
    sellerProducts: sellerProductsData,
    loading: sellerProductsLoading,
    refetch: refetchSellerProducts,
  } = useGetSellerProductsExperiences(lead?.id);

  // Use sellerLeadUUID from query param if it exists
  const partnershipReferralData = usePartnershipReferrals(sellerInputUUID);

  // TODO: Replace this with sellerJourney.sellerExperience === SellerExperience.AGENT_ACQUISITION
  // The presence of an agent referral no longer means that the user should see the agent experience
  // given that we now support agents referring but not representing sellers (aka true agent referral)
  const shouldSeeAgentExperience = !!(
    (partnershipReferralData.hasAgentReferral && !partnershipReferralData.isTrueAgentReferral) ||
    lead?.agentExperience
  );

  // TODO: Replace this with sellerJourney.sellerExperience === SellerExperience.AGENT_ACQUISITION
  const isAgentChannel = lead?.channel ? AGENT_CHANNELS.includes(lead?.channel) : false;

  // Blocking buyer content based on partnership blocking it or if the SellerExperience is AGENT_ACQUISITION
  const blockBuyerContent = partnershipReferralData.blockBuyerContent || isAgentChannel;

  // AgentReferralStep is skipped for the Opendoor App Evo Web View
  // Todo: Validate with T&O/Product if we can start showing it
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  const referralStep =
    partnershipReferralData.referralStep && !isOpendoorAppEvoWebView
      ? partnershipReferralData.referralStep
      : undefined;

  const { email, name, customerUUID: uuid } = getLeadInfo(lead);
  const contextualData = {
    sellerLeadUUID: lead?.id,
    sellerInputUUID,
    leadCustomer: {
      email,
      name,
      uuid,
    },
    market: lead?.address?.market?.toLowerCase(),
    isPartnershipLead: partnershipReferralData.isPartnershipLead,
    hasAgentReferral: partnershipReferralData.hasAgentReferral,
    isAgentChannel,
    blockBuyerContent,
    isBuyOnlyCustomer,
    customerLeads: customerLeads?.length ?? 0,
    shouldSeeAgentExperience,
  };

  const leadContext: ComponentProps<typeof AddContextualData>['leadContext'] = {
    sellerInputUuid: sellerInputUUID,
    sellerLeadUuid: lead?.id,
    sellerLeadMarket: lead?.address?.market?.toLowerCase(),
  };

  const loadingQualification =
    partnershipReferralData.loading ||
    sellerProductsLoading ||
    sellerLeadLoading ||
    participantsHelpers.getResult.loading;

  return (
    <QualificationContext.Provider
      value={{
        lead,
        zoneId,
        sellerInputUUID,
        loadingQualification,
        isOpendoorOfferQualified,
        blockBuyerContent,
        referralStep,
        participantsHelpers,
        addressUUID,
        customerLeads,
        isBuyOnlyCustomer,
        partnershipReferralData,
        shouldSeeAgentExperience,
        sellerProducts: sellerProductsData,
        refetchSellerProducts,
      }}
    >
      <AddContextualData data={contextualData} leadContext={leadContext}>
        {children}
      </AddContextualData>
    </QualificationContext.Provider>
  );
};
